import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DataService } from 'src/app/services/data.service';
import { PersonaService } from 'src/app/services/persona.service';
import { StepperService } from 'src/app/services/stepper.service';
import Swal from 'sweetalert2';
import { TipoDocumento } from 'src/app/models/enum/tipo-documento.enum';

@Component({
  selector: 'app-documentos-adjuntos',
  templateUrl: './documentos-adjuntos.component.html',
  styleUrls: ['./documentos-adjuntos.component.css']
})
export class DocumentosAdjuntosComponent implements OnInit {
  documentForm: FormArray<FormGroup<any>> = new FormArray<FormGroup<any>>([], { validators: [this.minThreeDocumentsOfTypeX] });
  estadoSituacionGuid: any;
  persona: any;
  tipo: TipoDocumento | any = TipoDocumento;
  formFirmado = false;

  constructor(private fb: FormBuilder, private dataService: DataService, private personaService: PersonaService, private stepperService: StepperService) {}

    getTipoBienesRaices(): string[] {
      return Object.keys(TipoDocumento);
    }

  ngOnInit() {
    this.personaService.persona$.subscribe({
      next: (persona) => {
        if (persona) {
          this.persona = persona;
          this.estadoSituacionGuid = persona.estadoSituacion.guid;
          this.documentForm = this.fb.array<FormGroup<any>>([]);
          if (persona.estadoSituacion.documentos?.length > 0) {
            for (let i = 0; i < persona.estadoSituacion.documentos?.length; i++) {
              this.agregarDocumento(persona.estadoSituacion.documentos[i]);
            }
          } else {
            this.agregarDocumento(null);
          }
          if (persona.estadoSituacion?.estadoFormularioSituacion == 'Firmado') {
            this.formFirmado = true;
            this.documentForm.disable();
          }
        }
      },
      error: (err) => console.log(err)
    });
  }

  onFileChange(event: any, index: number) {
    const file = event.target.files[0];
    if (file) {
      const allowedExtensions = ['.pdf', '.jpg'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        Swal.fire('Oops!', 'Ese tipo de archivos no esta permitido.', 'warning');
        this.documentForm.at(index).get('archivo')?.setValue(null);
      } else {
        this.documentForm.controls[index].patchValue({
          archivoBlob: file
        });
      }
    }
  }

  onSubmit() {
    if (this.persona.estadoSituacion?.estadoFormularioSituacion == 'Firmado') {
      this.stepperService.nextStep();
      return;
    }
    if (!this.documentForm.touched && this.persona.estadoSituacion.documentos?.length > 2) {
      this.stepperService.nextStep();
      return;
    }
    if (this.validaDocumentosTotales()) {
      this.removeNullGuid();

      var url = `estado-situacion/${this.personaService.getPersonaGuid()}/documentos`;
      this.dataService.postFile(this.generaFormData(), url).subscribe({
        next: (v) => {
          this.documentForm = this.fb.array<FormGroup<any>>([]);
          this.personaService.setPersona(); // Actualiza la persona después de guardar los documentos
        },
        error: (e) => {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text: "Ha ocurrido un error al guardar los documentos."
          });
        },
        complete: () => {
          Swal.fire({
            icon: "success",
            title: "Listo!",
            text: "Documentos guardados correctamente."
          });
          this.stepperService.nextStep();
        }
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: 'Recuerda que debes ingresar un mínimo de 3 liquidaciones o boletas de honorarios.'
      });
    }
  }

  agregarDocumento(documento: any): void {
    const nuevoDocumento = this.fb.group({
      guid: (documento?.guid),
      archivoBlob: (null),
      archivoNombre: (documento?.nombreArchivo),
      archivo: new FormControl(null, [Validators.required, this.formatoArchivoValido(['pdf', 'jpg'])]),
      tipo: new FormControl(documento?.tipoDocumento, [Validators.required])
    });

    if (documento != null) {
      nuevoDocumento.get('tipo')?.disable();
      this.documentForm.push(nuevoDocumento);
      return;
    }

    if (this.documentForm.length > 0 && this.documentForm.at(this.documentForm.length - 1).invalid && this.documentForm.at(this.documentForm.length - 1).get('guid')?.value == null) {
      this.documentForm.at(this.documentForm.length - 1).markAllAsTouched();
      return;
    } else {
      this.documentForm.push(nuevoDocumento);
    }
  }

  formatoArchivoValido(permitidas: string[]) {
    return (control: any) => {
      const archivo = control.value;
      if (!archivo) {
        return null; // No hay archivo, no hay error
      }

      const extension = archivo.split('.').pop().toLowerCase();
      if (permitidas.includes(extension)) {
        return null; // La extensión es válida, no hay error
      } else {
        return { formatoInvalido: true }; // La extensión no es válida, retorna un error
      }
    };
  }

  minThreeDocumentsOfTypeX(control: AbstractControl): ValidationErrors | null {
    const documentFormArray = control.get('documentForm') as FormArray;
    if (!documentFormArray) {
      return null;
    }

    const documentsOfTypeX = documentFormArray.controls
      .map((formGroup: any) => formGroup.get('tipo')?.value)
      .filter((tipo: string) => tipo === 'Liquidacion');

    if (documentsOfTypeX.length < 3) {
      return { minThreeDocumentsOfTypeX: true };
    }

    return null;
  }

  removeNullGuid() {
    for (let i = 0; i < this.documentForm.length; i++) {
      if (this.documentForm.at(i).get('guid')?.value == null) {
        this.documentForm.at(i).removeControl('guid');
      }
    }
  }

  eliminarDocumento(index: number, documento: any): void {
    if (documento.get('guid')?.value == null) {
      this.documentForm.removeAt(index);
      return;
    }
    Swal.fire({
      icon: 'question',
      title: 'Estas Seguro?',
      text: 'Estas seguro que deseas eliminar este documento?',
      showCancelButton: true,
      focusConfirm: false
    }).then((result) => {
      if (result.isConfirmed) {
        var url = `estado-situacion/${this.personaService.getPersonaGuid()}/documentos/${documento.get('guid')?.value}`;
        if (index >= 0 && index < this.documentForm.length) {
          this.dataService.deleteData(url).subscribe({
            next: (v) => {
              if (this.persona.estadoSituacion.documentos) {
                this.persona.estadoSituacion.documentos.length = this.persona.estadoSituacion.documentos.length - 1;
              }
              Swal.close();
            },
            error: (e) => {
              Swal.close();
              Swal.fire({
                icon: "error",
                title: "Oops!",
                text: "Ha ocurrido un error al borrar el documento."
              });
            }
          });
          this.documentForm.removeAt(index);
        }
      }
    });
  }

  volver() {
    this.stepperService.prevStep();
  }

  validaDocumentosTotales(): boolean {
    var totalLiquidaciones = 0;
    for (var control of this.documentForm.controls) {
      if (control.get('tipo')?.value == 'LiquidacionSueldo') {
        totalLiquidaciones++;
      }
    }
    return totalLiquidaciones > 2;
  }

  generaFormData(): FormData {
    const formData = new FormData();
    var index = 0;
    for (var control of this.documentForm.controls) {
      const guid = control.get('guid')?.value;
      if (guid != null) {
        continue;
      }
      if (control.get('archivoBlob')?.value != null) {
        formData.append('documentos[' + index + '].archivo', control.get('archivoBlob')?.value);
        formData.append('documentos[' + index + '].tipo', control.get('tipo')?.value);
        index++;
      }
    }
    return formData;
  }
}